import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import CreateStore from '../Prompt/CreateStore';
import { useUIStore } from '@/store/useUIStore';
import _Placeholder from './_Placeholder';
import IspCustomerForm from '../../../pages/Admin/Isp/IspCustomerForm';
import IspPlanForm from '../../../pages/Admin/Isp/IspPlanForm';
import IspPaymentMethodForm from '../../../pages/Admin/Isp/IspPaymentMethodForm';
import IspServerAdd from '../../../pages/Admin/Isp/IspServerAdd';
import IspServerDelete from '../../../pages/Admin/Isp/IspServerDelete';
import IspServerEdit from '../../../pages/Admin/Isp/IspServerEdit';
import IspNeedsSetup from '../../../pages/Admin/Isp/IspNeedsSetup';
import IspConfirm from '../../../pages/Admin/Isp/IspConfirm';
import IspCustomerDelete from '@/pages/Admin/Isp/IspCustomerDelete';
import IspAccountForm from '../../../pages/Admin/Isp/IspAccountForm';
import IspCustomerPicker from '../../../pages/Admin/Isp/IspCustomerPicker';
import IspPaymentForm from '../../../pages/Admin/Isp/IspPaymentForm';
import IspInvoicePrint from '../../../pages/Admin/Isp/IspInvoicePrint';
import IspApplicationReview from '../../../pages/Admin/Isp/IspApplicationReview';
import IspAccountEdit from '../../../pages/Admin/Isp/IspAccountEdit';
import IspProofReview from '../../../pages/Admin/Isp/IspProofReview';
import IspAccountInvoices from '../../../pages/Admin/Isp/IspAccountInvoices';
import UpdateStore from '../Prompt/UpdateStore';
import { TableStore } from '@/pages/Admin/Stores';
import ImportVoucher from '../Prompt/ImportVoucher';
import Verification from '../Prompt/Verification';
import CreateVoucher from '../Prompt/CreateVoucher';
import SubscribeStore from '../Prompt/SubscribeStore';
import { SelectedStore } from '@/pages/Admin/Subscription';
import Payout from '@/pages/Admin/Payout';
import PayoutForm from '../Prompt/PayoutForm';
import TransactionDetail from '../Prompt/TransactionDetail';
import EditVoucher from '../Prompt/EditVoucher';
import EditRate from '../Prompt/EditRate';
import DeviceSettings from '../Prompt/DeviceSettings';
import DeviceRegister from '../Prompt/DeviceRegister';
import type { SetupDraft } from '@/utils/setupDraft';
import SubVendoAdd from '../Prompt/SubVendoAdd';
import CreateVlanZone, { type CreateVlanZoneData } from '../Prompt/CreateVlanZone';
import ActivateLicense from '../Prompt/ActivateLicense';
import ManageLicense from '../Prompt/ManageLicense';
import OwnershipActionModal from '../Prompt/OwnershipActionModal';
import ReflashRouter from '../Prompt/ReflashRouter';
import RebindRouter from '../Prompt/RebindRouter';
import RebootRouter from '../Prompt/RebootRouter';
import SendFirmware from '../Prompt/SendFirmware';
import type { OwnershipClaim } from '@/api/vendo';
import SellDevice from '../Prompt/SellDevice';
import ReviewTransfer from '../Prompt/ReviewTransfer';
import ClaimDevice from '../Prompt/ClaimDevice';
import InquireCredits from '../Prompt/InquireCredits';
import InquireReseller from '../Prompt/InquireReseller';
import type { ApiPublicReseller } from '@/api/vendo';
import DistributorApply from '../Prompt/DistributorApply';
import ApplicationDocuments from '../Prompt/ApplicationDocuments';
import AddCredits from '../Prompt/AddCredits';
import ReceiptAccountForm, { type ReceiptAccountFormData } from '../Prompt/ReceiptAccountForm';
import BuyCreditPackage, { type BuyCreditPackageData } from '../Prompt/BuyCreditPackage';
import ConvertPointsModal, { type ConvertPointsModalData } from '../Prompt/ConvertPointsModal';
import ManageAccess, { type ManageAccessData } from '../Prompt/ManageAccess';
import CoinsAllModal from '../CoinsAllModal';
import EditFieldPrompt, { type EditFieldPromptData } from '../Prompt/EditFieldPrompt';
import PostMediaPicker, { type PostMediaPickerData } from '../Prompt/PostMediaPicker';
import ConfirmAction, { type ConfirmActionData } from '../Prompt/ConfirmAction';
import SupportTeam from '../Prompt/SupportTeam';
import RejectReasonForm, { type RejectReasonFormData } from '../Prompt/RejectReasonForm';
import type { AdminDistributorApplication } from '@/api/admin';
import { TableVouchers } from '@/pages/Admin/Vouchers';
import type { Rate } from '@/api/rate';
import type { VendoDevice } from '@/pages/Admin/Vendo';
import type { ApiVendoDevice, ApiDeviceTransfer } from '@/api/vendo';

interface Props<T = unknown> {
   show: boolean;
   template: string;
   title: string;
   data: T;
   actions?: {
      onConfirm?: (data: T) => void;
      onCancel?: () => void;
   };
}

// Templates whose modal must NOT close on backdrop click — the wizard
// flows here have multi-step state (flashing NodeMCU, pasting scripts)
// where an accidental dismiss costs real work to redo. Only the
// explicit X / Cancel button should close them.
const NON_DISMISSIBLE_TEMPLATES = new Set([
   'device-register',
   // Modals where backdrop-click closes too easily — operator loses
   // in-progress form data or the only displayed accept link.
   'device-settings',
   'sell-device',
   'review-transfer',
   'claim-device',
   // Rebind: the operator has a one-time claim code on screen from a box that
   // has just been wiped, and the flow then waits for that box to reconnect.
   // A stray backdrop tap mid-wait loses the code and means walking back to the
   // hardware to read a new one. Ray asked for this explicitly 2026-07-31.
   'rebind-router',
]);


/**
 * The register modal opens two ways: with a device to RESUME (from the "Setup
 * in progress" list) or with a setup DRAFT from smartfi.ph (the visitor chose
 * the board and typed the names before signing in). Both ride `data`.
 */
function registerData(data: unknown): { resumeDevice?: ApiVendoDevice; setupDraft?: SetupDraft } {
   const d = data as { setupDraft?: SetupDraft } | ApiVendoDevice | null | undefined;
   if (d && typeof d === 'object' && 'setupDraft' in d && d.setupDraft) return { setupDraft: d.setupDraft };
   return { resumeDevice: (d as ApiVendoDevice) || undefined };
}

const PromptWrapper = <T,>({
   show,
   template,
   title,
   data,
   actions,
}: Props<T>) => {
   const { handleClose } = useUIStore();
   const [formKey, setFormKey] = useState(0);

   // Reset form key when the form is opened
   useEffect(() => {
      if (show && template === 'create-voucher') {
         setFormKey((prev) => prev + 1);
      }
   }, [show, template]);

   if (!show) return null;

   const dismissOnBackdrop = !NON_DISMISSIBLE_TEMPLATES.has(template);

   return (
      <Wrapper>
         <Overlay onClick={dismissOnBackdrop ? () => handleClose() : undefined} />
         <Content>
            {template === 'payout' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <PayoutForm data={data as unknown} />
               </_Placeholder>
            )}
            {template === 'subscribe-store' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <SubscribeStore data={data as SelectedStore} />
               </_Placeholder>
            )}
            {template === 'create-store' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <CreateStore />
               </_Placeholder>
            )}
            {template === 'update-store' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <UpdateStore data={data as TableStore} />
               </_Placeholder>
            )}

            {template === 'verification' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <Verification
                     data={data as { data: T; msg: string }}
                     actions={actions}
                  />
               </_Placeholder>
            )}

            {template === 'import-voucher' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <ImportVoucher data={data as TableStore[]} />
               </_Placeholder>
            )}

            {template === 'transaction-detail' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <TransactionDetail data={data as any} />
               </_Placeholder>
            )}

            {template === 'edit-rate' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <EditRate data={data as Rate} />
               </_Placeholder>
            )}

            {template === 'edit-voucher' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <EditVoucher data={data as TableVouchers} />
               </_Placeholder>
            )}

            {template === 'create-voucher' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <CreateVoucher
                     key={`create-voucher-form-${formKey}`}
                     data={data as TableStore[]}
                  />
               </_Placeholder>
            )}

            {template === 'device-settings' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <DeviceSettings data={data as VendoDevice} />
               </_Placeholder>
            )}

            {template === 'device-register' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <DeviceRegister
                     resumeDevice={registerData(data).resumeDevice}
                     setupDraft={registerData(data).setupDraft}
                  />
               </_Placeholder>
            )}

            {/* ISP Business — add or edit a CUSTOMER. Uses the same shell as
                every other prompt in the panel rather than an inline panel of
                its own: an operator should not have to learn a second way for
                a form to appear. */}
            {template === 'isp-customer-form' && (
               <_Placeholder show={show} title={title} handleClose={handleClose}>
                  <IspCustomerForm
                     {...(data as React.ComponentProps<typeof IspCustomerForm>)}
                  />
               </_Placeholder>
            )}

            {template === 'isp-server-add' && (
               <_Placeholder show={show} title={title} handleClose={handleClose}>
                  <IspServerAdd
                     {...(data as React.ComponentProps<typeof IspServerAdd>)}
                  />
               </_Placeholder>
            )}

            {template === 'isp-account-invoices' && (
               <_Placeholder show={show} title={title} handleClose={handleClose}>
                  <IspAccountInvoices
                     {...(data as React.ComponentProps<typeof IspAccountInvoices>)}
                  />
               </_Placeholder>
            )}

            {/* Wide, because an invoice is a document rather than a form —
                squeezing it into a narrow modal makes the printed version look
                nothing like what the operator approved on screen. */}
            {template === 'isp-proof-review' && (
               <_Placeholder show={show} title={title} handleClose={handleClose}>
                  <IspProofReview
                     {...(data as React.ComponentProps<typeof IspProofReview>)}
                  />
               </_Placeholder>
            )}

            {template === 'isp-account-edit' && (
               <_Placeholder show={show} title={title} handleClose={handleClose}>
                  <IspAccountEdit
                     {...(data as React.ComponentProps<typeof IspAccountEdit>)}
                  />
               </_Placeholder>
            )}

            {template === 'isp-application-review' && (
               <_Placeholder show={show} title={title} handleClose={handleClose}>
                  <IspApplicationReview
                     {...(data as React.ComponentProps<typeof IspApplicationReview>)}
                  />
               </_Placeholder>
            )}

            {template === 'isp-invoice-print' && (
               <_Placeholder show={show} title={title} handleClose={handleClose}>
                  <IspInvoicePrint
                     {...(data as React.ComponentProps<typeof IspInvoicePrint>)}
                  />
               </_Placeholder>
            )}

            {template === 'isp-payment-form' && (
               <_Placeholder show={show} title={title} handleClose={handleClose}>
                  <IspPaymentForm
                     {...(data as React.ComponentProps<typeof IspPaymentForm>)}
                  />
               </_Placeholder>
            )}

            {template === 'isp-account-picker' && (
               <_Placeholder show={show} title={title} handleClose={handleClose}>
                  <IspCustomerPicker
                     {...(data as React.ComponentProps<typeof IspCustomerPicker>)}
                  />
               </_Placeholder>
            )}

            {template === 'isp-account-form' && (
               <_Placeholder show={show} title={title} handleClose={handleClose}>
                  <IspAccountForm
                     {...(data as React.ComponentProps<typeof IspAccountForm>)}
                  />
               </_Placeholder>
            )}

            {template === 'isp-confirm' && (
               <_Placeholder show={show} title={title} handleClose={handleClose}>
                  <IspConfirm
                     {...(data as React.ComponentProps<typeof IspConfirm>)}
                  />
               </_Placeholder>
            )}

            {/* Deleting a customer who still holds lines: the modal lists them
                and disconnects them, rather than refusing and sending the
                operator to a button that no longer exists. */}
            {template === 'isp-customer-delete' && (
               <_Placeholder show={show} title={title} handleClose={handleClose}>
                  <IspCustomerDelete
                     {...(data as React.ComponentProps<typeof IspCustomerDelete>)}
                  />
               </_Placeholder>
            )}

            {template === 'isp-needs-setup' && (
               <_Placeholder show={show} title={title} handleClose={handleClose}>
                  <IspNeedsSetup
                     {...(data as React.ComponentProps<typeof IspNeedsSetup>)}
                  />
               </_Placeholder>
            )}

            {template === 'isp-server-edit' && (
               <_Placeholder show={show} title={title} handleClose={handleClose}>
                  <IspServerEdit
                     {...(data as React.ComponentProps<typeof IspServerEdit>)}
                  />
               </_Placeholder>
            )}

            {template === 'isp-server-delete' && (
               <_Placeholder show={show} title={title} handleClose={handleClose}>
                  <IspServerDelete
                     {...(data as React.ComponentProps<typeof IspServerDelete>)}
                  />
               </_Placeholder>
            )}

            {template === 'isp-plan-form' && (
               <_Placeholder show={show} title={title} handleClose={handleClose}>
                  <IspPlanForm
                     {...(data as React.ComponentProps<typeof IspPlanForm>)}
                  />
               </_Placeholder>
            )}

            {template === 'isp-payment-method-form' && (
               <_Placeholder show={show} title={title} handleClose={handleClose}>
                  <IspPaymentMethodForm
                     {...(data as React.ComponentProps<typeof IspPaymentMethodForm>)}
                  />
               </_Placeholder>
            )}

            {template === 'sub-vendo-add' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <SubVendoAdd data={data as VendoDevice} />
               </_Placeholder>
            )}

            {template === 'create-vlan-zone' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <CreateVlanZone data={data as CreateVlanZoneData} />
               </_Placeholder>
            )}

            {template === 'activate-license' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <ActivateLicense
                     data={data as { device: VendoDevice; onBrowsePartners: () => void }}
                  />
               </_Placeholder>
            )}

            {template === 'manage-license' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <ManageLicense data={data as { device: VendoDevice }} />
               </_Placeholder>
            )}

            {template === 'ownership-action' && (
               <_Placeholder show={show} title={title} handleClose={handleClose}>
                  <OwnershipActionModal
                     data={data as { device: VendoDevice; claim: OwnershipClaim }}
                  />
               </_Placeholder>
            )}

            {template === 'reflash-router' && (
               <_Placeholder show={show} title={title} handleClose={handleClose}>
                  <ReflashRouter data={data as { device: VendoDevice }} />
               </_Placeholder>
            )}

            {template === 'rebind-router' && (
               <_Placeholder show={show} title={title} handleClose={handleClose}>
                  <RebindRouter data={data as { device: VendoDevice }} />
               </_Placeholder>
            )}

            {/* Canary: send one firmware version to chosen devices. Kept apart
                from Activate, which declares the fleet default. */}
            {template === 'send-firmware' && (
               <_Placeholder show={show} title={title} handleClose={handleClose}>
                  <SendFirmware data={data as { id: string; version: string; target?: 'router' | 'nodemcu' | 'bundle' }} />
               </_Placeholder>
            )}

            {template === 'reboot-router' && (
               <_Placeholder show={show} title={title} handleClose={handleClose}>
                  <RebootRouter data={data as { device: VendoDevice }} />
               </_Placeholder>
            )}

            {template === 'sell-device' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <SellDevice data={data as { device: VendoDevice }} />
               </_Placeholder>
            )}

            {template === 'review-transfer' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <ReviewTransfer data={data as { transfer: ApiDeviceTransfer }} />
               </_Placeholder>
            )}

            {template === 'claim-device' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <ClaimDevice />
               </_Placeholder>
            )}

            {template === 'inquire-credits' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <InquireCredits data={data as { id?: string } | undefined} />
               </_Placeholder>
            )}

            {template === 'inquire-reseller' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <InquireReseller data={data as ApiPublicReseller | undefined} />
               </_Placeholder>
            )}

            {template === 'distributor-apply' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <DistributorApply />
               </_Placeholder>
            )}

            {template === 'receipt-account-form' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <ReceiptAccountForm data={data as ReceiptAccountFormData} />
               </_Placeholder>
            )}

            {template === 'buy-credit-package' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <BuyCreditPackage data={data as BuyCreditPackageData} />
               </_Placeholder>
            )}

            {template === 'support-team' && (
               <_Placeholder show={show} title={title} handleClose={handleClose}>
                  <SupportTeam />
               </_Placeholder>
            )}

            {template === 'confirm-action' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <ConfirmAction data={data as ConfirmActionData} />
               </_Placeholder>
            )}

            {template === 'post-media-picker' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <PostMediaPicker data={data as PostMediaPickerData} />
               </_Placeholder>
            )}

            {template === 'edit-field' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <EditFieldPrompt data={data as EditFieldPromptData} />
               </_Placeholder>
            )}

            {template === 'reject-reason' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <RejectReasonForm data={data as RejectReasonFormData} />
               </_Placeholder>
            )}

            {template === 'application-documents' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <ApplicationDocuments data={data as AdminDistributorApplication} />
               </_Placeholder>
            )}

            {template === 'add-credits' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <AddCredits />
               </_Placeholder>
            )}

            {template === 'convert-points' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <ConvertPointsModal data={data as ConvertPointsModalData} />
               </_Placeholder>
            )}

            {template === 'manage-access' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <ManageAccess data={data as ManageAccessData} />
               </_Placeholder>
            )}

            {template === 'coins-all' && (
               <_Placeholder
                  show={show}
                  title={title}
                  handleClose={handleClose}
               >
                  <CoinsAllModal
                     data={data as { source: 'operator' | 'admin' }}
                  />
               </_Placeholder>
            )}
         </Content>
      </Wrapper>
   );
};

const Wrapper = styled.div`
   position: fixed;
   top: 0;
   left: 0;
   width: 100%;
   height: 100vh;
   height: 100dvh;
   z-index: 1500;
`;
const Overlay = styled.div`
   position: fixed;
   top: 0;
   left: 0;
   width: 100%;
   height: 100vh;
   height: 100dvh;
   background-color: rgba(0, 0, 0, 0.5);
   z-index: 1500;
`;
const Content = styled.div`
   position: absolute;
   top: 50%;
   left: 50%;
   transform: translate(-50%, -50%);
   z-index: 1501;

   @media (max-width: 768px) {
      top: auto;
      bottom: 0;
      left: 0;
      transform: none;
      width: 100%;
   }
`;

export default PromptWrapper;
